import axios from 'axios';
import * as types from '../mutation-types';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {Connection} from '@/types';
import type {PlatformConnectionsMap} from '@/types/platform';

export interface PlatformConnectionsState {
    loading: Record<string, boolean>,
}

const state: PlatformConnectionsState = {
    loading: {
        get: false,
        delete: false,
        toggle: false,
        matching: false,
        create_unavailability: false,
    },
};

const getters = {
    loading: (state: PlatformConnectionsState) => state.loading,
};

const mutations = {
    [types.SET_PLATFORM_CONNECTIONS_LOADING](state: PlatformConnectionsState, loading: {
        subject: string,
        loading: boolean
    }) {
        state.loading[loading.subject] = loading.loading;
    },
};

function resolvePlatformConnections(responseData: {
    platform_connections?: PlatformConnectionsMap,
}): PlatformConnectionsMap {
    return responseData.platform_connections ?? {};
}

const actions = {
    fetchPlatformConnections({commit}) {
        commit('SET_PLATFORM_CONNECTIONS_LOADING', {subject: 'get', loading: true});

        axios.get('/v1/connections')
            .then((response) => {
                const platformConnections = resolvePlatformConnections(response?.data ?? {});

                commit('outdoorsy/SET_OUTDOORSY_CONNECTIONS', platformConnections.outdoorsy ?? [], {root: true});
                commit('hqRentals/SET_HQRENTALS_CONNECTIONS', platformConnections['hq-rentals'] ?? [], {root: true});
                commit('twistedRoad/SET_TWISTED_ROAD_CONNECTIONS', platformConnections.twisted_road ?? [], {root: true});
            }).catch(() => {
        }).finally(() => {
            commit('SET_PLATFORM_CONNECTIONS_LOADING', {subject: 'get', loading: false});
        });
    },

    async deleteConnection({commit}, connection: Connection): Promise<{ provider: any } | undefined> {
        try {
            const responseData = await performApiRequest(commit, 'SET_PLATFORM_CONNECTIONS_LOADING', 'delete', () => axios.delete(`/v1/connections/${connection.uuid}`));

            return {provider: responseData.provider};
        } catch {
            return undefined;
        }
    },

    async toggleConnectionActive({commit}, connection: Connection) {
        await performApiRequest(commit, 'SET_PLATFORM_CONNECTIONS_LOADING', 'toggle', () => axios.put(`/v1/connections/${connection.uuid}/toggle-active`));
    },

    matchListing({commit, rootGetters}, {provider_uuid, l_id}) {
        commit('SET_PLATFORM_CONNECTIONS_LOADING', {subject: 'matching', loading: true});

        return axios.post(`/v1/company/${rootGetters['company/company'].uuid}/provider-listings/${provider_uuid}/match/listings/${l_id}`)
            .then((response) => {
                return {listings: response?.data?.listings ?? []};
            }).catch(() => {
                return undefined;
            }).finally(() => {
                commit('SET_PLATFORM_CONNECTIONS_LOADING', {subject: 'matching', loading: false});
            });
    },

    unMatchProviderListing({commit}, {l_id, provider_uuid}) {
        commit('SET_PLATFORM_CONNECTIONS_LOADING', {subject: 'saveListing1', loading: true});

        return axios.delete(`/v1/listings/${l_id}/provider-listings/${provider_uuid}`)
            .then(() => {
                return {provider_uuid};
            }).catch(() => {
                return undefined;
            }).finally(() => {
                commit('SET_PLATFORM_CONNECTIONS_LOADING', {subject: 'saveListing1', loading: false});
            });
    },

    createListingUnavailability({commit, rootGetters}, data) {
        commit('SET_PLATFORM_CONNECTIONS_LOADING', {subject: 'create_unavailability', loading: true});

        axios.post(`/v1/company/${rootGetters['company/company'].uuid}/provider-bookings`, data)
            .then((response) => {
                if (response?.data?.booking) {
                    commit('booking/SET_CURRENT_WORKING_BOOKING', response.data.booking, {root: true});
                }
            }).catch(() => {
        }).finally(() => {
            commit('SET_PLATFORM_CONNECTIONS_LOADING', {subject: 'create_unavailability', loading: false});
        });
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
